import {
  Text,
  Checkbox,
  FormControlLabel,
  IconButton,
  Popover,
} from '@ltvco/refresh-lib/theme';
import { Close, Search, KeyboardArrowRight } from '@mui/icons-material';
import React, {
  useState,
  useMemo,
  useCallback,
  useEffect,
  useRef,
} from 'react';

import {
  type SelectedJurisdictions,
  type JurisdictionTabKey,
} from '../../../types';
import {
  JURISDICTION_TABS,
  getJurisdictionsForTab,
  filterJurisdictionsBySearch,
  getSelectedJurisdictionsCount,
  getTotalJurisdictionsCount,
  selectCurrentTabJurisdictions,
  clearCurrentTabJurisdictions,
  toggleJurisdiction,
  useTabScrolling,
  clearAllJurisdictions,
} from '../../../utils';

import {
  PopoverContainer,
  Header,
  Title,
  TabsContainer,
  StyledTabs,
  StyledTab,
  TabScrollIndicator,
  ContentBody,
  SearchInputContainer,
  SearchInput,
  SearchIconContainer,
  ControlsRow,
  ClearAllButton,
  JurisdictionsList,
  JurisdictionItem,
  Footer,
  SelectedCount,
  FooterButtons,
  CancelButton,
  ApplyButton,
  EmptyState,
} from './components';

// ============= TYPES =============
export interface JurisdictionFilterPopoverProps {
  /** Anchor element for the popover */
  anchorEl: HTMLElement | null;
  /** Whether the popover is open */
  open: boolean;
  /** Callback when popover is closed */
  onClose: () => void;
  /** Currently selected jurisdictions */
  selectedJurisdictions: SelectedJurisdictions;
  /** Callback when jurisdictions are applied */
  onApply: (selected: SelectedJurisdictions) => void;
  /** Test ID for the component */
  testId?: string;
}

// ============= COMPONENT =============
export const JurisdictionFilterPopover: React.FC<
  JurisdictionFilterPopoverProps
> = ({
  anchorEl,
  open,
  onClose,
  selectedJurisdictions,
  onApply,
  testId = 'jurisdiction-filter-popover',
}) => {
  const [activeTab, setActiveTab] = useState<number>(0);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [localSelected, setLocalSelected] = useState<SelectedJurisdictions>(
    selectedJurisdictions
  );
  const tabsRef = useRef<HTMLDivElement>(null);

  // Use the tab scrolling hook to handle tab scrolling logic
  const { showMoreIndicator } = useTabScrolling(tabsRef, open);

  useEffect(() => {
    if (open) {
      setLocalSelected(selectedJurisdictions);
      setSearchQuery('');
      setActiveTab(0);
    }
  }, [open, selectedJurisdictions]);

  const currentTabKey = JURISDICTION_TABS[activeTab]?.key as JurisdictionTabKey;

  const currentTabJurisdictions = useMemo(() => {
    if (!currentTabKey) {
      return [];
    }
    return getJurisdictionsForTab(currentTabKey);
  }, [currentTabKey]);

  const filteredJurisdictions = useMemo(() => {
    return filterJurisdictionsBySearch(currentTabJurisdictions, searchQuery);
  }, [currentTabJurisdictions, searchQuery]);

  const selectedCount = getSelectedJurisdictionsCount(localSelected);
  const totalCount = getTotalJurisdictionsCount();
  const allTabSelected = currentTabJurisdictions.every(
    (j) => localSelected[j.id]
  );

  const handleTabChange = useCallback(
    (_event: React.SyntheticEvent, newValue: number) => {
      setActiveTab(newValue);
      setSearchQuery('');
    },
    []
  );

  const handleSelectAllTab = useCallback(() => {
    if (allTabSelected) {
      setLocalSelected(
        clearCurrentTabJurisdictions(localSelected, currentTabKey)
      );
    } else {
      setLocalSelected(
        selectCurrentTabJurisdictions(localSelected, currentTabKey)
      );
    }
  }, [allTabSelected, localSelected, currentTabKey]);

  const handleClearAll = useCallback(() => {
    setLocalSelected(clearAllJurisdictions());
  }, []);

  const handleToggleJurisdiction = useCallback((jurisdictionId: string) => {
    setLocalSelected((prev) => toggleJurisdiction(prev, jurisdictionId));
  }, []);

  const handleApply = useCallback(() => {
    onApply(localSelected);
    onClose();
  }, [localSelected, onApply, onClose]);

  const handleCancel = useCallback(() => {
    setLocalSelected(selectedJurisdictions);
    onClose();
  }, [selectedJurisdictions, onClose]);

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'left',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'left',
      }}
      marginThreshold={8}
      slotProps={{
        paper: {
          sx: (theme) => ({
            mt: 1,
            maxWidth: { xs: 'calc(100vw - 16px)', sm: 'none' },
            [theme.breakpoints.only('xs')]: {
              left: '8px',
              right: '8px',
            },
          }),
        },
      }}
      data-testid={testId}
    >
      <PopoverContainer>
        {/* Header */}
        <Header>
          <Title variant="h6">Add Jurisdiction</Title>
          <IconButton size="small" onClick={handleCancel} aria-label="Close">
            <Close />
          </IconButton>
        </Header>

        {/* Tabs */}
        <TabsContainer ref={tabsRef}>
          <StyledTabs
            value={activeTab}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons={false}
          >
            {JURISDICTION_TABS.map((tab) => (
              <StyledTab key={tab.key} label={tab.label} />
            ))}
          </StyledTabs>
          {showMoreIndicator && (
            <TabScrollIndicator>
              <KeyboardArrowRight sx={{ fontSize: '1rem' }} />
            </TabScrollIndicator>
          )}
        </TabsContainer>

        {/* Content Body */}
        <ContentBody>
          {/* Search Input */}
          <SearchInputContainer>
            <SearchIconContainer>
              <Search sx={{ fontSize: '1.25rem' }} />
            </SearchIconContainer>
            <SearchInput
              fullWidth
              placeholder="Search Jurisdiction Name"
              value={searchQuery}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setSearchQuery(e.target.value)
              }
            />
          </SearchInputContainer>

          {/* Controls Row */}
          <ControlsRow>
            <FormControlLabel
              control={
                <Checkbox
                  checked={allTabSelected}
                  indeterminate={
                    currentTabJurisdictions.some((j) => localSelected[j.id]) &&
                    !allTabSelected
                  }
                  onChange={handleSelectAllTab}
                  size="small"
                />
              }
              label="Selected All"
              sx={{
                '& .MuiFormControlLabel-label': {
                  fontSize: '0.875rem',
                  fontWeight: 500,
                },
              }}
            />
            <ClearAllButton onClick={handleClearAll}>Clear All</ClearAllButton>
          </ControlsRow>

          {/* Jurisdictions List */}
          <JurisdictionsList>
            {filteredJurisdictions.length === 0 ? (
              <EmptyState>
                <Text variant="body2" sx={{ color: 'text.secondary' }}>
                  No jurisdictions found matching "{searchQuery}"
                </Text>
              </EmptyState>
            ) : (
              filteredJurisdictions.map((jurisdiction) => (
                <JurisdictionItem key={jurisdiction.id}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={localSelected[jurisdiction.id] || false}
                        onChange={() =>
                          handleToggleJurisdiction(jurisdiction.id)
                        }
                        size="small"
                      />
                    }
                    label={
                      <Text variant="body2" sx={{ fontSize: '0.875rem' }}>
                        {jurisdiction.name}
                      </Text>
                    }
                  />
                </JurisdictionItem>
              ))
            )}
          </JurisdictionsList>
        </ContentBody>

        {/* Footer */}
        <Footer>
          <SelectedCount>
            {selectedCount} of {totalCount} jurisdictions selected
          </SelectedCount>
          <FooterButtons>
            <CancelButton variant="outlined" onClick={handleCancel}>
              Cancel
            </CancelButton>
            <ApplyButton variant="contained" onClick={handleApply}>
              Apply
            </ApplyButton>
          </FooterButtons>
        </Footer>
      </PopoverContainer>
    </Popover>
  );
};
